import {
  type ActivityIdentity,
  activityAttribution,
} from '@borradh-workspace/observability/activity-attribution';
import posthog, { type CaptureResult } from 'posthog-js';
import { isImpersonatingTab } from './posthog-impersonation';

let identity: ActivityIdentity | undefined;
let personProperties: Record<string, unknown> | undefined;
let lastIdentifiedKey: string | undefined;

export const getBrowserActivityProps = () =>
  activityAttribution(identity ?? { impersonation: isImpersonatingTab() });

/** Covers pageviews, autocapture, custom actions and errors in this tab. */
export function stampActivityAttribution(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event) return event;
  return {
    ...event,
    properties: { ...event.properties, ...getBrowserActivityProps() },
  };
}

/** May run before the provider initializes; keep the verified identity ready. */
export function setPostHogActivityIdentity(
  next: ActivityIdentity,
  properties?: Record<string, unknown>
) {
  identity = next;
  personProperties = properties;
  applyPostHogActivityIdentity();
}

export function applyPostHogActivityIdentity() {
  if (!posthog.__loaded || !identity?.userId) return;
  const props = getBrowserActivityProps();
  // Register before identify, so the identify event carries the same actor.
  posthog.register(props);
  posthog.identify(identity.userId, personProperties);
  const key = JSON.stringify([
    identity.userId,
    identity.impersonatedBy,
    identity.isAdmin,
    identity.organizationId,
  ]);
  if (key !== lastIdentifiedKey) {
    lastIdentifiedKey = key;
    // One verified app visit per page load/identity change. This also covers
    // a read-only visit whose first pageview preceded the session response.
    posthog.capture('app_session_identified', props);
  }
}

export function clearPostHogActivityIdentity() {
  identity = undefined;
  personProperties = undefined;
  lastIdentifiedKey = undefined;
}
